/**
 * Fixed-timestep game loop with render interpolation.
 *
 * WHY THIS IS NOT NEGOTIABLE (see ARCHITECTURE.md):
 *  1. Physics is deterministic, which is what makes tests/sim/ possible at all.
 *  2. Balance is identical on a 144Hz monitor and a 60Hz tablet.
 *  3. It structurally prevents frame-rate-dependent tuning - the single most
 *     common bug introduced into a game loop.
 *
 * Simulation code receives a CONSTANT dt. If you ever find yourself passing the
 * real frame delta into a system, something has gone wrong. Presentation-only
 * code (camera smoothing, VFX) may use the real delta via `onRender`.
 */

import { tuning, FIXED_DT } from './tuning';

/**
 * How many fixed steps `pending` seconds of accumulated time buys, and what is
 * left over.
 *
 * Extracted so slow motion can be tested against the real arithmetic instead of
 * a copy of it. A parallel implementation in a test would keep passing while the
 * shipped loop broke - the same trap `tests/sim/` exists to avoid.
 */
export function planFixedSteps(
  pending: number,
  maxSteps: number,
): { steps: number; remainder: number } {
  let steps = 0;
  let remainder = pending;
  while (remainder >= FIXED_DT && steps < maxSteps) {
    remainder -= FIXED_DT;
    steps++;
  }
  // Hitting the cap means we are running slower than real time. Drop the backlog
  // rather than accumulating debt forever.
  if (steps >= maxSteps) remainder = 0;
  return { steps, remainder };
}

export interface LoopCallbacks {
  /** Advance the simulation by exactly FIXED_DT seconds. Called 0..n times per frame. */
  onFixedStep: (dt: number) => void;
  /**
   * Draw. `alpha` is the 0-1 blend between the previous and current sim state,
   * for interpolating visuals. `realDt` is wall-clock seconds since last render.
   */
  onRender: (alpha: number, realDt: number) => void;
}

export class GameLoop {
  readonly fixedDt = FIXED_DT;

  private accumulator = 0;
  private lastTime = 0;
  private rafHandle = 0;
  private running = false;

  /** Rolling frame-time stats, consumed by qualityTiers.ts to auto-downgrade. */
  private frameTimes: number[] = [];
  private frameTimeCursor = 0;
  private static readonly FRAME_SAMPLE_COUNT = 120;

  /** Simulation steps executed since start. Handy for sim tests and debug HUD. */
  stepCount = 0;

  /**
   * Slow motion, for Cat Instinct. This scales how fast real time ENTERS the
   * accumulator - it never touches the `dt` a system receives, which stays
   * FIXED_DT forever. Rendering, camera, audio and HUD keep running at real time.
   */
  private timeScale = 1;

  constructor(private readonly callbacks: LoopCallbacks) {
    this.frameTimes = new Array<number>(GameLoop.FRAME_SAMPLE_COUNT).fill(FIXED_DT);
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastTime = performance.now();
    this.accumulator = 0;
    this.rafHandle = requestAnimationFrame(this.frame);
  }

  stop(): void {
    this.running = false;
    if (this.rafHandle) cancelAnimationFrame(this.rafHandle);
    this.rafHandle = 0;
  }

  get isRunning(): boolean {
    return this.running;
  }

  /** 1 is real time. Below 1 slows the simulation without changing its `dt`. */
  setTimeScale(scale: number): void {
    this.timeScale = Math.max(0, scale);
  }

  get currentTimeScale(): number {
    return this.timeScale;
  }

  private frame = (now: number): void => {
    if (!this.running) return;
    this.rafHandle = requestAnimationFrame(this.frame);

    let realDt = (now - this.lastTime) / 1000;
    this.lastTime = now;

    // Guard against a huge delta after a tab is backgrounded or the debugger
    // pauses. Without this the accumulator would demand hundreds of catch-up
    // steps and the game would appear to freeze while it "fast-forwards".
    const maxDelta = FIXED_DT * tuning.sim.maxCatchUpSteps;
    if (realDt > maxDelta) realDt = maxDelta;
    if (realDt < 0) realDt = 0;

    // Frame-time stats measure the REAL frame, not the slowed one, or slow
    // motion would look like a performance win and step the quality tier up.
    this.recordFrameTime(realDt);

    const plan = planFixedSteps(
      this.accumulator + realDt * this.timeScale,
      tuning.sim.maxCatchUpSteps,
    );
    for (let step = 0; step < plan.steps; step++) {
      this.callbacks.onFixedStep(FIXED_DT);
      this.stepCount++;
    }
    this.accumulator = plan.remainder;

    const alpha = this.accumulator / FIXED_DT;
    this.callbacks.onRender(alpha, realDt);
  };

  private recordFrameTime(dt: number): void {
    this.frameTimes[this.frameTimeCursor] = dt;
    this.frameTimeCursor = (this.frameTimeCursor + 1) % GameLoop.FRAME_SAMPLE_COUNT;
  }

  /** Mean frame time in seconds over the sample window. */
  get averageFrameTime(): number {
    let total = 0;
    for (const t of this.frameTimes) total += t;
    return total / this.frameTimes.length;
  }

  /** 95th-percentile frame time in seconds. The number that actually matters -
   *  a good average with bad p95 still feels like stuttering. */
  get p95FrameTime(): number {
    const sorted = [...this.frameTimes].sort((a, b) => a - b);
    const index = Math.min(sorted.length - 1, Math.floor(sorted.length * 0.95));
    return sorted[index] ?? FIXED_DT;
  }

  get fps(): number {
    const avg = this.averageFrameTime;
    return avg > 0 ? 1 / avg : 0;
  }
}

/**
 * Headless driver for tests and the level validator. Runs `steps` fixed
 * simulation steps as fast as possible with no renderer and no RAF.
 */
export function runHeadless(steps: number, onFixedStep: (dt: number) => void): void {
  for (let i = 0; i < steps; i++) onFixedStep(FIXED_DT);
}

/** Converts seconds to a whole number of fixed steps (at least one). */
export const secondsToSteps = (seconds: number): number =>
  Math.max(1, Math.round(seconds / FIXED_DT));
